// Zwei-Faktor-Anmeldung ein- und ausschalten — per Authenticator-App oder E-Mail.
//
// Authenticator-App (Google Authenticator, Microsoft Authenticator …):
//   Passwort → QR-Code scannen → ersten Code bestätigen → Ersatzcodes sichern.
//   Erst mit dem bestätigten Code ist sie scharf; die Ersatzcodes erscheinen
//   genau einmal.
// E-Mail: bewusst zweistufig — erst geht ein Code an die Adresse, dann wird er
//   bestätigt. Sonst sperrt sich aus, wer sich in der Adresse vertippt.
import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { istNativ } from '../../../gemeinsam/lib/plattform';
import { Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Stand = {
  aktiv: boolean;
  methode: 'app' | 'mail' | null;
  ersatzcodesUebrig: number | null;
  mailMaske: string | null;
  versandBereit: boolean;
  hinterlegteMail: string | null;
  aktiviertAm: string | null;
};

type Schritt = 'ruhe' | 'mail' | 'code' | 'abschalten' | 'appPasswort' | 'appScan' | 'ersatz' | 'ersatzPasswort';
type Einrichtung = { vorgangId: string; geheimnis: string; qr: string };

export function Zweifaktor() {
  const { t } = useSprache();
  const { daten: stand, neuLaden } = useDaten<Stand>('/auth/2fa');

  const [schritt, setSchritt] = useState<Schritt>('ruhe');
  const [mail, setMail] = useState('');
  const [code, setCode] = useState('');
  const [vorgangId, setVorgangId] = useState('');
  const [maske, setMaske] = useState('');
  const [passwort, setPasswort] = useState('');
  const [einrichtung, setEinrichtung] = useState<Einrichtung | null>(null);
  const [ersatzcodes, setErsatzcodes] = useState<string[]>([]);
  const [gesichert, setGesichert] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  function zurueck() {
    setSchritt('ruhe');
    setCode(''); setPasswort(''); setFehler(null); setEinrichtung(null);
  }

  async function los<T>(arbeit: () => Promise<T>, standardFehler: string) {
    setSendet(true); setFehler(null); setMeldung(null);
    try { return await arbeit(); } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : standardFehler);
      return undefined;
    } finally { setSendet(false); }
  }

  // --- E-Mail -------------------------------------------------------------
  async function codeAnfordern() {
    const r = await los(() => api<{ vorgangId: string; mailMaske: string }>('/auth/2fa/starten', {
      methode: 'POST', daten: { mail: mail.trim() || undefined, passwort },
    }), 'Der Code konnte nicht verschickt werden.');
    if (r) { setVorgangId(r.vorgangId); setMaske(r.mailMaske); setPasswort(''); setSchritt('code'); }
  }

  async function mailEinschalten() {
    const r = await los(() => api('/auth/2fa/aktivieren', { methode: 'POST', daten: { vorgangId, code: code.trim() } }), 'Der Code stimmt nicht.');
    if (r !== undefined) { zurueck(); setMeldung(t('zfa.eingeschaltet')); await neuLaden(); }
  }

  // --- Authenticator-App --------------------------------------------------
  async function appStarten() {
    const r = await los(() => api<Einrichtung>('/auth/2fa/app/starten', { methode: 'POST', daten: { passwort } }), 'Das Passwort stimmt nicht.');
    if (r) { setEinrichtung(r); setPasswort(''); setCode(''); setSchritt('appScan'); }
  }

  async function appEinschalten() {
    if (!einrichtung) return;
    const r = await los(() => api<{ ersatzcodes: string[] }>('/auth/2fa/app/aktivieren', {
      methode: 'POST', daten: { vorgangId: einrichtung.vorgangId, code: code.trim() },
    }), 'Der Code stimmt nicht.');
    if (r) { setErsatzcodes(r.ersatzcodes); setGesichert(false); setKopiert(false); setEinrichtung(null); setCode(''); setSchritt('ersatz'); await neuLaden(); }
  }

  async function ersatzNeu() {
    const r = await los(() => api<{ ersatzcodes: string[] }>('/auth/2fa/ersatzcodes', { methode: 'POST', daten: { passwort } }), 'Das Passwort stimmt nicht.');
    if (r) { setErsatzcodes(r.ersatzcodes); setGesichert(false); setKopiert(false); setPasswort(''); setSchritt('ersatz'); await neuLaden(); }
  }

  async function ausschalten() {
    const r = await los(() => api('/auth/2fa/deaktivieren', { methode: 'POST', daten: { passwort } }), 'Das Passwort stimmt nicht.');
    if (r !== undefined) { zurueck(); setMeldung(t('zfa.ausgeschaltet')); await neuLaden(); }
  }

  async function kopieren() {
    try { await navigator.clipboard.writeText(ersatzcodes.join('\n')); setKopiert(true); } catch { /* Zwischenablage gesperrt — die Codes stehen ja da */ }
  }

  function herunterladen() {
    const text = `LagerPro — Ersatzcodes für die Zwei-Faktor-Anmeldung\n${new Date().toLocaleString()}\n\n${ersatzcodes.join('\n')}\n\nJeder Code gilt genau einmal.\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'lagerpro-ersatzcodes.txt'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  if (!stand) return <Karte><div className="flex justify-center p-8"><Spinner /></div></Karte>;

  const passwortFeld = (beschriftung: string) => (
    <Feld label={beschriftung} type="password" value={passwort} autoFocus autoComplete="current-password"
          onChange={e => setPasswort(e.target.value)} />
  );
  const status = stand.aktiv
    ? `${t('zfa.aktiv')} · ${stand.methode === 'app' ? t('zfa.methodeApp') : `${t('zfa.methodeMail')} ${stand.mailMaske ?? ''}`}`
    : t('zfa.aus');

  return (
    <Karte>
      <KartenKopf titel={t('zfa.einstellung')} hinweis={status} />
      <div className="space-y-4 p-5">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{t('zfa.erklaerungBeide')}</p>

        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <Erfolgsmeldung text={meldung} />

        {schritt === 'ruhe' && (
          <>
            {stand.aktiv && stand.methode === 'app' && stand.ersatzcodesUebrig !== null && (
              <p className={`text-sm ${stand.ersatzcodesUebrig <= 3 ? 'text-amber-700 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'}`}>
                {t('zfa.ersatzUebrig').replace('{n}', String(stand.ersatzcodesUebrig))}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {!stand.aktiv && (
                <>
                  <Knopf variante="primaer" onClick={() => { setPasswort(''); setSchritt('appPasswort'); }}>{t('zfa.mitApp')}</Knopf>
                  <Knopf disabled={!stand.versandBereit} onClick={() => { setMail(stand.hinterlegteMail ?? ''); setPasswort(''); setSchritt('mail'); }}>
                    {t('zfa.mitMail')}
                  </Knopf>
                </>
              )}
              {stand.aktiv && stand.methode === 'mail' && (
                <Knopf variante="primaer" onClick={() => { setPasswort(''); setSchritt('appPasswort'); }}>{t('zfa.zurApp')}</Knopf>
              )}
              {stand.aktiv && stand.methode === 'app' && (
                <Knopf onClick={() => { setPasswort(''); setSchritt('ersatzPasswort'); }}>{t('zfa.ersatzNeu')}</Knopf>
              )}
              {stand.aktiv && <Knopf variante="gefahr" onClick={() => setSchritt('abschalten')}>{t('zfa.ausschalten')}</Knopf>}
            </div>
            {!stand.aktiv && <p className="text-xs text-slate-500 dark:text-slate-400">{t('zfa.appEmpfohlen')}</p>}
            {!stand.versandBereit && !stand.aktiv && <Hinweis art="warnung">{t('zfa.keinVersand')}</Hinweis>}
          </>
        )}

        {schritt === 'mail' && (
          <div className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <Feld label={t('zfa.mailFrage')} type="email" value={mail} autoFocus onChange={e => setMail(e.target.value)} />
            <Feld label={t('zfa.passwortWeiter')} type="password" value={passwort} autoComplete="current-password"
                  onChange={e => setPasswort(e.target.value)} />
            <div className="flex gap-2">
              <Knopf variante="primaer" laedt={sendet} disabled={!passwort} onClick={codeAnfordern}>{t('zfa.codeSenden')}</Knopf>
              <Knopf onClick={zurueck}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </div>
        )}

        {schritt === 'code' && (
          <div className="space-y-3 rounded-xl border border-marke-300 p-4 dark:border-marke-800">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t('zfa.gesendet')} <strong className="text-slate-800 dark:text-slate-200">{maske}</strong>
            </p>
            <Feld label={t('zfa.code')} value={code} inputMode="numeric" autoFocus autoComplete="one-time-code"
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            <div className="flex gap-2">
              <Knopf variante="primaer" laedt={sendet} disabled={code.length !== 6} onClick={mailEinschalten}>{t('zfa.bestaetigen')}</Knopf>
              <Knopf onClick={zurueck}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </div>
        )}

        {(schritt === 'appPasswort' || schritt === 'ersatzPasswort') && (
          <form onSubmit={e => { e.preventDefault(); void (schritt === 'appPasswort' ? appStarten() : ersatzNeu()); }}
                className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            {schritt === 'ersatzPasswort' && <p className="text-sm text-slate-600 dark:text-slate-400">{t('zfa.ersatzNeuFrage')}</p>}
            {passwortFeld(t('zfa.passwortWeiter'))}
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet} disabled={!passwort}>{t('zfa.weiter')}</Knopf>
              <Knopf type="button" onClick={zurueck}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </form>
        )}

        {schritt === 'appScan' && einrichtung && (
          <form onSubmit={e => { e.preventDefault(); void appEinschalten(); }}
                className="space-y-4 rounded-xl border border-marke-300 p-4 dark:border-marke-800">
            <ol className="list-decimal space-y-1 ps-5 text-sm text-slate-700 dark:text-slate-300">
              <li>{t('zfa.schritt1')}</li>
              <li>{t('zfa.scannen')}</li>
              <li>{t('zfa.schritt3')}</li>
            </ol>
            <div className="flex flex-wrap items-center gap-5">
              {/* Der QR-Code bleibt immer schwarz auf weiß — dunkle Codes lesen viele Kameras nicht. */}
              <div className="rounded-lg bg-white p-2 [&>svg]:h-44 [&>svg]:w-44" dangerouslySetInnerHTML={{ __html: einrichtung.qr }} />
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('zfa.manuell')}</p>
                <code className="block select-all break-all rounded-md bg-slate-100 px-2 py-1.5 font-mono text-sm tracking-wider text-slate-900 dark:bg-slate-800 dark:text-slate-100">
                  {einrichtung.geheimnis}
                </code>
              </div>
            </div>
            <Feld label={t('zfa.appCode')} value={code} inputMode="numeric" autoComplete="one-time-code" autoFocus
                  className="max-w-[12rem] text-center text-xl tracking-[0.3em]"
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet} disabled={code.length !== 6}>{t('zfa.bestaetigen')}</Knopf>
              <Knopf type="button" onClick={zurueck}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </form>
        )}

        {schritt === 'ersatz' && (
          <div className="space-y-3 rounded-xl border border-amber-300 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/20">
            <p className="font-semibold text-slate-900 dark:text-slate-100">{t('zfa.ersatzTitel')}</p>
            <p className="text-sm text-slate-700 dark:text-slate-300">{t('zfa.ersatzHinweis')}</p>
            <ul className="grid grid-cols-2 gap-2 font-mono text-sm tracking-wider text-slate-900 sm:grid-cols-5 dark:text-slate-100">
              {ersatzcodes.map(c => <li key={c} className="rounded-md bg-white px-2 py-1.5 text-center dark:bg-slate-900">{c}</li>)}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Knopf onClick={kopieren}>{kopiert ? t('zfa.kopiert') : t('zfa.kopieren')}</Knopf>
              {!istNativ() && <Knopf onClick={herunterladen}>{t('zfa.herunterladen')}</Knopf>}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={gesichert} onChange={e => setGesichert(e.target.checked)} />
              {t('zfa.gesichert')}
            </label>
            <Knopf variante="primaer" disabled={!gesichert}
                   onClick={() => { setErsatzcodes([]); setSchritt('ruhe'); setMeldung(t('zfa.eingeschaltet')); }}>
              {t('zfa.fertig')}
            </Knopf>
          </div>
        )}

        {schritt === 'abschalten' && (
          <form onSubmit={e => { e.preventDefault(); void ausschalten(); }}
                className="space-y-3 rounded-xl border border-rose-300 p-4 dark:border-rose-900">
            {passwortFeld(t('zfa.passwortFrage'))}
            <div className="flex gap-2">
              <Knopf type="submit" variante="gefahr" laedt={sendet} disabled={!passwort}>{t('zfa.ausschalten')}</Knopf>
              <Knopf type="button" onClick={zurueck}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </form>
        )}
      </div>
    </Karte>
  );
}
